import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import useGetSecurities from 'hooks/useGetSecurities';
import { adaptListOfBundledDocuments, getListOfBundledDocumentsColumns } from '../components/BundledDocuments/ListOfBundledDocuments/utils';
import { getProcessOptions, PROCESS_VALUE_TO_KEY } from './useBundleDocuments';

const GET_SECURITIES = gql`
    query SearchSecurities($ids: [Int!], $take: Int = 100, $skip: Int = 0) {
        securities(take: $take, skip: $skip, where: { id: { in: $ids } }) {
            items {
                id
                name
                isin
            }
        }
    }
`;

const useListOfBundledDocuments = ({
    bundledDocumentId,
    productOptions,
    handleViewDetails,
    isDocDataLoading = false,
}: {
    bundledDocumentId: string;
    productOptions: {
        value: string;
        label: string;
    }[];
    handleViewDetails?: ({ item }: { item: any }) => void;
    isDocDataLoading?: boolean;
}) => {
    const { t, i18n: { language } } = useTranslation();

    const {
        data: allStaticDocumentsInABundleFromAPI,
        isLoading: isAllStaticDocumentsInABundleLoading,
        error: allStaticDocumentsInABundleLoadingError,
    } = useGetCustomService({
        service: 'customDocumentsService',
        api: 'getAllStaticDocumentsInABundle',
        payload: { bundleId: bundledDocumentId, language },
        enabled: !!bundledDocumentId,
        refetchOnWindowFocus: false,
    });

    const { securityIds } = useMemo(() => {
        const documents = allStaticDocumentsInABundleFromAPI?.data?.documents || [];

        return {
            securityIds: documents.map((doc) => doc?.securityId).filter(Boolean),
        };
    }, [allStaticDocumentsInABundleFromAPI?.data?.documents]);

    const {
        data: securitiesData,
        loading: securitiesLoading,
        error: securitiesError,
    } = useGetSecurities({
        loadInitially: securityIds.length > 0,
        ids: securityIds || [],
        take: securityIds?.length,
        query: GET_SECURITIES,
        options: { staleTime: 60 * 60 * 1000, gcTime: 60 * 60 * 1000 },
    });

    const allStaticDocumentsInABundle = allStaticDocumentsInABundleFromAPI?.data || {};

    const listOfBundledDocuments = useMemo(() => adaptListOfBundledDocuments({
        data: allStaticDocumentsInABundle?.documents,
        securities: securitiesData || [],
        onViewDetails: handleViewDetails,
        t,
        isLoading: isDocDataLoading,
    }), [
        bundledDocumentId,
        allStaticDocumentsInABundle?.documents,
        securitiesData,
        t,
        handleViewDetails,
        isDocDataLoading,
    ]);
    const productLabels = new Map(productOptions.map(({ value, label }) => [String(value), label]));
    const productTitle = useMemo(() => {
        if (!allStaticDocumentsInABundle?.productId) return '';
        const label = productLabels.get(String(allStaticDocumentsInABundle?.productId));

        return `${label} ${allStaticDocumentsInABundle?.name}`;
    }, [productLabels, allStaticDocumentsInABundle]);

    const uploadModalTitle = useMemo(() => {
        if (!allStaticDocumentsInABundle?.productId) return '';
        const label = productLabels.get(String(allStaticDocumentsInABundle?.productId));
        const processName = getProcessOptions({ t }).find((option) => allStaticDocumentsInABundle?.key?.includes(PROCESS_VALUE_TO_KEY[option.value]))?.label;

        return `${processName} > ${label}`;
    }, [productLabels, allStaticDocumentsInABundle, t]);

    return {
        productTitle,
        uploadModalTitle,
        listOfBundledDocuments,
        listOfBundledDocumentsColumns: getListOfBundledDocumentsColumns({ t }),
        ListOfBundledDocumentsLoading: isAllStaticDocumentsInABundleLoading || securitiesLoading,
        ListOfBundledDocumentsLoadingError: allStaticDocumentsInABundleLoadingError || securitiesError,
    };
};

export default useListOfBundledDocuments;
